<template>
  <div class="product-fields">
    <div class="tabs-container">
      <div class="tabs swiper-container" ref="tabs">
        <div class="tabs-wrapper swiper-wrapper">
          <a
            v-for="tab in tabs"
            :key="tab.code"
            href="#"
            class="tab swiper-slide"
            :class="{ 'tab--active': activeTab === tab.code }"
            @click.prevent="handleTabClick(tab.code, $event)"
            >{{ tab.label }}</a
          >
        </div>
      </div>
      <div class="tabs-scrollbar swiper-scrollbar" ref="scrollbar"></div>

      <!-- <nav class="tabs" ref="tabs">
				<a
					v-for="tab in tabs"
					:key="tab.code"
					href="#"
					class="tab"
					:class="{ 'tab--active': activeTab === tab.code }"
					@click.prevent="handleTabClick(tab.code, $event)"
					>{{ tab.label }}</a
				>
			</nav> -->
    </div>
    <div class="container">
      <div class="content" ref="reflow">
        <transition-group name="content" mode="out-in">
          <div
            class="product-details"
            v-show="activeTab === 'product_details'"
            key="product_details"
          >
            <div class="layout">
              <div class="column column--left">
                <h2 class="title h3">{{ fields.product_details_title2 }}</h2>
              </div>
              <div class="column column--right column--columnize">
                <div v-html="fields.product_details_body_text" />
                <div class="batch">
                  {{ fields.product_details_batch_number }}
                </div>
                <ul class="clean-labels" v-if="productDetailLabels.length > 0">
                  <li
                    class="clean-labels__item"
                    v-for="label in productDetailLabels"
                    :key="label.label"
                  >
                    <clean-label v-bind="label" />
                  </li>
                </ul>

                <img
                  v-if="fields.product_details_certifications"
                  :src="fields.product_details_certifications"
                  class="certification-image"
                />
              </div>
            </div>
          </div>
          <div v-show="activeTab === 'faq'" key="faq">
            <div class="layout">
              <div class="column column--left">
                <h2 class="title h3">{{ fields.faq_title2 }}</h2>
              </div>
              <div class="column column--right">
                <ul class="faq__list">
                  <li v-for="item in faq" :key="item.n" class="faq__list__item">
                    <div
                      tabindex="0"
                      class="faq__question"
                      :class="{ 'faq__question--active': activeFaq === item.n }"
                      @click="handleFaqClick(item.n)"
                      @keyup.space.prevent="handleFaqClick(item.n)"
                      @keyup.enter="handleFaqClick(item.n)"
                    >
                      {{ item.question }}
                    </div>
                    <smooth-reflow>
                      <div
                        class="faq__answer"
                        v-show="activeFaq === item.n"
                        v-html="item.answer"
                      />
                    </smooth-reflow>
                  </li>
                </ul>
              </div>
            </div>
          </div>
          <div v-show="activeTab === 'subscription'" key="subscription">
            <div class="layout">
              <div class="column column--left">
                <h2 class="title h3">
                  {{ $translations.products.product.subscription }}
                </h2>
              </div>
              <div
                class="column column--right column--columnize"
                v-html="subscriptionDetails"
              ></div>
            </div>
          </div>
          <div
            class="benefits"
            v-show="activeTab === 'benefits'"
            key="benefits"
          >
            <div class="layout">
              <div class="column column--left">
                <h2 class="title h3">{{ fields.benefits_title2 }}</h2>
              </div>
              <div class="column column--right column--columnize">
                <div v-html="fields.benefits_body_text" />
              </div>
            </div>
          </div>

          <div v-show="activeTab === 'reviews'" key="reviews">
            <div class="reviews" ref="reviews" />
          </div>
        </transition-group>
      </div>
    </div>
    <!-- <ingredients-slider :ingredients="ingredients" ref="ingredientsSlider" /> -->
    <slider ref="ingredientsSlider" :slides="slides" v-if="slides.length > 0" />
  </div>
</template>

<script>
import smoothReflow from "vue-smooth-reflow";
import SmoothReflow from "../smooth-reflow.vue";
import IngredientsSlider from "./ingredients_slider.vue";
import Slider from "../slider.vue";
import CleanLabel from "./clean-label.vue";
import Swiper from "swiper/dist/js/swiper.js";
export default {
  mixins: [smoothReflow],
  name: "ProductFields",
  selector: "#vue-product-fields",
  components: { IngredientsSlider, CleanLabel, SmoothReflow, Slider },
  data() {
    return {
      activeTab:
        Object.entries(this.fields).length === 0
          ? "reviews"
          : "product_details",
      activeFaq: null,
      reviewsEl: null,
    };
  },
  props: {
    fields: {
      type: Object,
      required: true,
    },
    subscriptionDetails: {
      type: String,
      required: true,
    },
    showSubscriptionTab: {
      type: Boolean,
      default: false,
    },
    usingYotpo: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    slides() {
      return this.ingredients.map((ingredient) => ({
        backgroundImage: ingredient.image,
        smallBackgroundImage: ingredient.responsiveImage,
        headline: ingredient.ingredient,
        body: ingredient.description,
      }));
    },
    productDetailLabels() {
      if (!this.fields.product_details_clean_labels) return [];
      return this.fields.product_details_clean_labels
        .split(";")
        .map((label) => {
          const splitted = label.split("=");
          return {
            label: splitted[0],
            info: splitted[1],
          };
        });
    },
    faq() {
      // maybe optimize this later (no need for premature optimization)
      // the fields objects have fields with key `faq_a1`, `faq_a2`, `faq_q1` etc.
      // we need to all and combine them. There can be n question and answers.
      // Only invariant is that if and only if `faq_qn` exists so does `faq_an`.
      const keyRe = /^faq_q(\d+)$/;
      const faq = Object.entries(this.fields).reduce((acc, [key, value]) => {
        const matches = key.match(keyRe);
        if (!matches) return acc;
        const n = parseInt(matches[1], 10);
        return [
          ...acc,
          {
            n,
            question: value,
            answer: this.fields[`faq_a${n}`],
          },
        ];
      }, []);
      return faq.sort((a, b) => a.n - b.n);
    },
    tabs() {
      if (Object.entries(this.fields).length === 0) {
        return [
          {
            code: "reviews",
            label: this.$translations.products.product.reviews,
          },
        ];
      }
      const tabs = [
        {
          code: "product_details",
          label: this.fields.product_details_title,
        },
        {
          code: "ingredients",
          label: this.fields.ingredients_title,
        },
        {
          code: "faq",
          label: this.fields.faq_title,
        },
        {
          code: "reviews",
          label: this.$translations.products.product.reviews,
        },
        {
          code: "subscription",
          label: this.$translations.products.product.subscription,
        },
        {
          code: "benefits",
          label: this.fields.benefits_title,
        },
      ];
      return this.showSubscriptionTab
        ? tabs
        : tabs.filter((tab) => tab.code !== "subscription");
    },
    ingredients() {
      // this could be done better, but im in pain and i dont want to think right now
      const ingredients = [];
      for (let i = 1; i < 11; i++) {
        if (!(`ingredients_ingredient${i}` in this.fields)) break;
        ingredients.push({
          image: this.fields[`ingredients_image${i}`],
          responsiveImage: this.fields[`ingredients_image_responsive${i}`],
          description: this.fields[`ingredients_description${i}`],
          ingredient: this.fields[`ingredients_ingredient${i}`],
        });
      }
      return ingredients;
    },
  },
  methods: {
    handleFaqClick(n) {
      // if already selected faq item then collapse, otherwise set
      this.activeFaq = this.activeFaq === n ? null : n;
    },
    handleTabClick(code, evt) {
      switch (code) {
        case "ingredients":
          if (this.$refs.ingredientsSlider) {
            window.scrollToPosition(this.$refs.ingredientsSlider.$el.offsetTop);
          }
          break;
        default:
          this.activeTab = code;
        // scroll tab into center
        // this.$refs.tabs.scrollTo({
        // 	left:
        // 		evt.target.offsetLeft -
        // 		evt.target.computedStyleMap().get("margin-left").value -
        // 		(this.$refs.tabs.getBoundingClientRect().width -
        // 			evt.target.getBoundingClientRect().width) /
        // 			2,
        // 	behavior: "smooth"
        // });
      }
    },
  },
  mounted() {
    this.slider = new Swiper(this.$refs.tabs, {
      slidesPerView: "auto",
      freeMode: true,
      spaceBetween: 56,
      slidesOffsetAfter: 16,
      centerInsufficientSlides: true,
      mousewheel: true,
      scrollbar: {
        el: this.$refs.scrollbar,
        hide: false,
      },
    });

    if (this.usingYotpo) {
      const yotpoReviews = document.querySelector("[data-yotpo-reviews]");
      const reviews = yotpoReviews.cloneNode(true);
      yotpoReviews.remove();
      reviews.style = "";
      this.$refs.reviews.appendChild(reviews);

      // hijack star rating click and scroll to and open reviews
      const ratingLink = document.querySelector("[data-star-rating]");
      ratingLink.addEventListener("click", (evt) => {
        evt.preventDefault();
        evt.stopPropagation();
        this.activeTab = "reviews";
        window.scrollToPosition(this.$el.offsetTop - 50);
      });
    } else {
      const originalReviews = document.getElementById(
        "shopify-product-reviews"
      );
      const reviews = originalReviews.cloneNode(true);
      originalReviews.remove();
      reviews.style = "";
      this.$refs.reviews.appendChild(reviews);
    }

    this.$smoothReflow({
      el: this.$refs.reflow,
      transitionEvent: {
        selector: "div",
        propertyName: "opacity",
      },
    });
  },
};
</script>

<style lang="scss" scoped>
.container {
  @include inner-container();
}
.tabs-container {
  @include inner-container();
  @include media("<=tablet") {
    padding-left: 0;
    padding-right: 0;
  }
}
.tabs {
  margin-top: 24px;
  display: flex;
  justify-content: center;
  font-family: $font-family-mono;
  font-size: 12px;
  letter-spacing: 2px;
  line-height: 26px;
  text-transform: uppercase;
  padding-left: 16px;
  padding-right: 16px;
}
.tabs-scrollbar {
  width: 100%;
  height: 1px;
  border-radius: 0;
  width: calc(100% - 32px);
  margin: 8px auto 0 auto;
  display: none;
  @include media("<=desktop") {
    display: block;
  }
}
.tab {
  width: auto;
  color: black;
  text-decoration: none;
  transition: color 0.15s ease-in-out;
  &:hover {
    color: darken($teal, 10%);
  }
  &--active {
    color: $teal;
  }
}
.content {
  margin-top: 64px;
  padding-bottom: 64px;
  position: relative;
  @include media("<=tablet") {
    margin-top: 24px;
  }
  &-leave-active,
  &-enter-active {
    transition: opacity 0.2s ease-in-out;
  }
  &-enter-active {
    transition-delay: 0.2s;
  }
  &-leave-active {
    position: absolute;
    top: 0;
  }
  &-enter,
  &-leave-to {
    opacity: 0;
  }
  /* &-enter-active,
	&-leave-active {
		transition: opacity 10s ease-in-out;
		position: absolute;
	}
	&-leave,
	&-leave-active {
		position: absolute;
	}
	&-enter,
	&-leave-to {
		opacity: 0;
	} */
}
.layout {
  display: flex;
  justify-content: space-between;
  @include media("<=tablet") {
    display: block;
  }
}
.title {
  margin-top: 0;
}
.column {
  &--left {
    width: calc(33.3% - 28px);
    @include media("<=tablet") {
      width: 100%;
    }
  }
  &--right {
    width: calc(66.7% - 28px);
    > :first-child {
      margin-top: 0;
    }
    @include media("<=tablet") {
      width: 100%;
    }
  }
  &--columnize {
    column-count: 2;
    column-gap: 28px;
    @include media("<=tablet") {
      column-count: 1;
    }
  }
  ::v-deep h3 {
    margin-top: 0;
    font-size: 20px;
  }
}
.clean-labels {
  margin: 0;
  padding: 0;
  list-style: none;
  margin-top: 32px;
  &__item {
    & + & {
      margin-top: 8px;
    }
  }
}
.certification-image {
  margin-top: 24px;
  width: 78px;
}
.faq {
  &__list {
    margin: 0;
    padding: 0;
    list-style: none;
    &__item {
      & + & {
        margin-top: 24px;
      }
    }
  }
  &__question {
    font-size: 20px;
    color: #32302e;
    letter-spacing: 0;
    line-height: 24px;
    position: relative;
    transition: color 0.2s ease-in-out;
    &:hover,
    &:focus {
      /* outline: 1px solid blue; */
      outline: none;
      color: $teal-hover;
    }
    &::before {
      content: "+";
      position: absolute;
      top: 50%;
      left: -40px;
      transform: translateY(-50%);
    }
    &--active {
      color: $teal;
      &::before {
        content: "-";
      }
    }
  }
  &__answer {
    padding: 16px 0 40px 0;
    ::v-deep {
      > :first-child {
        margin-top: 0;
      }
      > :last-child {
        margin-bottom: 0;
      }
    }
  }
}
::v-deep {
  #shopify-product-reviews {
    margin: 0;
  }
  .spr {
    &-container {
      padding: 0;
      border: none;
      display: grid;
      grid-template-columns: 1fr 2fr;
      grid-template-rows: auto;
      grid-column-gap: 32px;
      @include media("<=tablet") {
        display: block;
      }
    }
    &-pagination {
      a {
        color: $teal;
        text-decoration: none;
        transition: box-shadow 0.15s ease-in;
        box-shadow: inset 0 -0.6em 0 transparent;
        &:hover {
          box-shadow: inset 0 -0.6em 0 $link-hover;
        }
      }
    }
    &-icon {
      color: #ffe074;
    }
    &-header {
      grid-column-start: 1;
      grid-column-end: 1;
    }
    &-header-title {
      margin-top: 0;
      font-size: 32px;
    }
    &-summary {
      margin-top: 32px;
    }
    &-summary-actions-newreview {
      float: none;
      display: table; // <- weird inline with break
      margin-top: 16px;
      color: $teal;
      text-decoration: none;
      transition: box-shadow 0.15s ease-in;
      box-shadow: inset 0 -0.6em 0 transparent;
      &:hover {
        box-shadow: inset 0 -0.6em 0 $link-hover;
      }
    }
    &-review-reportreview {
      display: none;
    }
    &-content {
    }
  }
}
</style>
